import { useEffect, useState } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import NotFoundPage from "@/pages/NotFoundPage";
import { isChunkLoadError } from "@/lib/errorClassification";
import { attemptStaleRecoveryReload, checkServerVersion } from "@/lib/versionManager";
import { RouteErrorScreen } from "./RouteErrorScreen";
import { StaleVersionScreen } from "./VersionMismatchFallback";

/**
 * The root route's errorElement.
 *
 * Only a real routing 404 reaches NotFoundPage. Every other throw is asked the
 * one question that separates the two causes: is this tab running the build the
 * server is serving? A deploy replaces every hashed chunk and changes the
 * backend contract underneath an open tab, so a stale build recovers by
 * reloading; a current build has a genuine defect and says so. Routing either
 * to the 404 page is what made visitors see "page not found" after a release.
 */
type Phase = "stale" | "broken";

export function RouteErrorBoundary() {
  const error = useRouteError();

  const isRouteResponse = isRouteErrorResponse(error);
  const isChunkFailure = !isRouteResponse && isChunkLoadError(error);

  const [phase, setPhase] = useState<Phase>(isChunkFailure ? "stale" : "broken");

  useEffect(() => {
    if (isRouteResponse) return;

    if (isChunkFailure) {
      void checkServerVersion();
      attemptStaleRecoveryReload();
      return;
    }

    let cancelled = false;

    void checkServerVersion()
      .then((isOutdated) => {
        if (cancelled) return;
        if (isOutdated) {
          attemptStaleRecoveryReload();
          setPhase("stale");
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [isRouteResponse, isChunkFailure]);

  if (isRouteResponse) {
    return <NotFoundPage />;
  }

  if (phase === "stale") {
    return <StaleVersionScreen />;
  }

  return <RouteErrorScreen />;
}

export default RouteErrorBoundary;
